import { sum, pluck } from 'ramda';
import { createSelector } from 'reselect';

import { AscendState } from '../index';
import { formSelector } from '../../components/Onboard/ClientAgreement/ContentContainer/Content';
import { getUserState } from '../user';
import { getAllDebtsData, getDebtState } from '../debt';

export const getInitialContractLoading = createSelector(
  getUserState,
  getDebtState,
  (user, debt) => user.loading || debt.loading
);

export const getInitialContractData = createSelector(
  getUserState,
  getAllDebtsData,
  (user, debts) => ({
    currentPage: user.data.currentPage,
    firstName: user.data.firstName,
    middleName: user.data.middleName,
    lastName: user.data.lastName,
    name: `${user.data.firstName} ${user.data.lastName}`,
    email: user.data.email,
    phone: user.data.phone,
    ssn: user.data.ssn,
    dateOfBirth: user.data.dateOfBirth,
    streetAddress: user.data.streetAddress,
    city: user.data.city,
    state: user.data.state,
    zip: user.data.zip,
    governmentId: user.data.governmentId,
    contactFirstName: user.data.contactFirstName,
    contactLastName: user.data.contactLastName,
    contactSSN: user.data.contactSSN,
    contactDateOfBirth: user.data.contactDateOfBirth,
    bankName: user.data.bankName,
    accountType: user.data.accountType,
    accountHolderName: user.data.accountHolderName,
    accountHolderStreetAddress: user.data.accountHolderStreetAddress,
    accountHolderCity: user.data.accountHolderCity,
    accountHolderState: user.data.accountHolderState,
    accountHolderZip: user.data.accountHolderZip,
    draftOption: user.data.draftOption,
    initialDraftDate: user.data.initialDraftDate,
    draftAmount: Number(user.data.draftAmount).toFixed(2),
    initialDraftAmount: Number(user.data.initialDraftAmount).toFixed(2),
    recurringDraftDate: user.data.recurringDraftDate,
    dateSigned: user.data.dateSigned,
    clientInitials: user.data.clientInitials,
    routingNumber: user.data.routingNumber,
    accountNumber: user.data.accountNumber,
    estimatedProgramFeePercent: user.data.ascendFee ? user.data.ascendFee.toString() : '15',
    advisorName: user.data.advisorName,
    clientSignature: user.data.clientSignature,
    debts: debts.length > 0 ? debts : [{}],
    csaUrl: user.data.csaUrl,
    stateFull: user.data.stateFull,
    programLengthMonths: user.data.programLengthMonths,
    programLengthPayments: user.data.programLengthPayments,
  })
);

export const getShowDummyDataPayoff = createSelector(getUserState, (user) => user.data.showDummyDataPayoff);
export const getShowDummyData = createSelector(getUserState, (user) => user.data.showDummyData);
export const getSignupMapping = createSelector(getUserState, (user) => user.data.signupMapping);
export const getCsaUrl = createSelector(getUserState, (user) => user.data.csaUrl);
export const getState = createSelector(getUserState, (user) => user.data.stateFull);
export const getAddress = createSelector(getUserState, (user) => user.data.streetAddress);

export const getDebtsFromForm = (state: AscendState) => formSelector(state, 'debts') || [];
export const getAscendFeeFromForm = (state: AscendState) =>
  formSelector(state, 'estimatedProgramFeePercent') || '15';

export const getMonthlySavingsFromForm = (state: AscendState) =>
  formSelector(state, 'draftAmount') || '';

export const getTotalDebt = createSelector(getUserState, getDebtsFromForm, (user, debts) => {
  if (debts && debts.length > 0 && debts[0].balanceAtEnrollment) {
    const balances = debts.map((debt: any) =>
      debt.balanceAtEnrollment ? Number(noCommas(debt.balanceAtEnrollment.toString())) : 0
    );
    return sum(balances);
  } else {
    return user.data.estimatedEnrolledDebt ? user.data.estimatedEnrolledDebt : 0;
  }
});

export const getEstimatedSettlementAmount = createSelector(
  getTotalDebt,
  getUserState,
  (totalDebt, user) =>
    (Number(totalDebt) * Number(user.data.estimatedSettlePercent) * 0.01).toFixed(2)
);

export const getEstimatedProgramFee = createSelector(
  getTotalDebt,
  getAscendFeeFromForm,
  (totalDebt, feePercent) => (Number(totalDebt) * Number(feePercent) * 0.01).toFixed(2)
);

const noCommas = (value: string) => value.replace(/[,]/g, '');
const cftPerMonth = 12.5;
export const getEstimatedProgramLength = createSelector(
  getTotalDebt,
  getUserState,
  getAscendFeeFromForm,
  getMonthlySavingsFromForm,
  (totalDebt, user, feePercent, monthlySavings) => {
    const totalFees = (Number(feePercent) + Number(user.data.estimatedSettlePercent)) * 0.01;
    const monthlySavingsNumber = Number(noCommas(monthlySavings));
    if (!isNaN(monthlySavingsNumber) && !isNaN(Number(totalDebt))) {
      return Math.ceil(
        (Number(totalDebt) * totalFees) / (monthlySavingsNumber - cftPerMonth)
      ).toString();
    } else {
      return '?';
    }
  }
);

export const getEstimatedProgramSavings = createSelector(
  getTotalDebt,
  getEstimatedSettlementAmount,
  getEstimatedProgramFee,
  (totalDebt, settlementAmount, programFee) =>
    (Number(totalDebt) - Number(settlementAmount) - Number(programFee)).toFixed(2)
);

export const getNonEmptyDebts = createSelector(getDebtsFromForm, (debts) => {
  return debts.length > 0 ? debts : [{ test: '3' }];
});
